// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

// --- Services ---
import userManagementSvc from "../../services/api/userManagement";

// --- Models ---
import { UserItem, UserFilter } from "../../models/types/user/user-managerment";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import ResetPassword from "./ResetPassword";
import DialogComponent from "../../components/dialog";

// --- Utils ---
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

// --- Icons ---
import { Pencil, KeyRound, Trash, LoaderCircle } from "lucide-react";
import { MediaUrl } from "../../utils/configuration";

export default function UserManagementPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();
    const [data, setData] = useState<UserItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<UserItem | null>(null);
    const [isOpenResetPassword, setIsOpenResetPassword] = useState<boolean>(false);
    const [statusLoadingIds, setStatusLoadingIds] = useState<Set<string>>(new Set());
    const [twoFALoadingIds, setTwoFALoadingIds] = useState<Set<string>>(new Set());

    const getListAll = async (params: DefaultFilterRequest<UserFilter>) => {
        setLoading(true);
        try {
            const res = await userManagementSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t("common.error"), t("userManagement.notifications.error"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: UserItem) => {
        try {
            const res = await userManagementSvc.deleteUser(row.id);
            if (res && res.content.status) {
                notify(t("common.success"), t("userManagement.notifications.deleteSuccess"), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t("common.error"), t("userManagement.notifications.error"), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<UserFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name") || undefined,
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: UserItem) => {
        navigate(UrlCollection.EditUser.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: UserItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const handleChangePassword = (row: UserItem) => {
        setSelectedRow(row);
        setIsOpenResetPassword(true);
    }

    const handleCloseResetPassword = () => {
        setIsOpenResetPassword(false);
    }

    const handleChangeStatusActive = async (item: UserItem) => {
        setStatusLoadingIds(new Set(statusLoadingIds).add(item.id));
        try {
            const res = await (item.status ? userManagementSvc.deactiveUser(item.id) : userManagementSvc.activeUser(item.id));
            if (res && res.content.status) {
                notify(t("common.success"), item.status ? t("userManagement.notifications.deactivateSuccess") : t("userManagement.notifications.activateSuccess"), "success");
                fetchData();
            }
        } catch (error) {
            notify(t("common.error"), t("userManagement.notifications.error"), "danger");
        } finally {
            setStatusLoadingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(item.id);
                return newSet;
            });
        }
    }

    const handleChangeStatus2FA = async (item: UserItem) => {
        setTwoFALoadingIds(new Set(twoFALoadingIds).add(item.id));
        try {
            const res = await userManagementSvc.disable2FA(item.id);
            if (res && res.content.status) {
                notify("Thành công", "Vô hiệu hóa 2FA thành công", "success");
                fetchData();
            }
        } catch (error) {
            notify("Lỗi", "Xảy ra lỗi", "danger");
        } finally {
            setTwoFALoadingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(item.id);
                return newSet;
            });
        }
    }

    const columns = [
        { header: t("userManagement.table.stt"), accessor: "index", sortable: false, className: "w-[50px]" },
        {
            header: t("userManagement.table.avatar"),
            accessor: (row: UserItem) => <img src={row.avatar ? MediaUrl + row.avatar : "/assets/images/user-avatar.png"} alt={row.fullName} className="w-10 h-10 rounded-full" />,
            sortable: false,
            className: "max-w-[50px]"
        },
        { header: t("userManagement.table.fullName"), accessor: "fullName", sortable: true },
        { header: t("userManagement.table.email"), accessor: "email", sortable: true },
        { header: t("userManagement.table.role"), accessor: "roleName", sortable: true },
        {
            header: t("userManagement.table.modifiedDate"),
            accessor: (row: UserItem) => `${dayjs(row.modified_date).format("DD/MM/YYYY")}`,
            sortable: true,
        },
        {
            header: "Trạng thái 2FA",
            accessor: (row: UserItem) => {
                const isLoading = twoFALoadingIds.has(row.id);

                return row.isUse2FA ? (
                    <button
                        onClick={() => handleChangeStatus2FA(row)}
                        disabled={isLoading}
                        className="min-w-[80px] flex items-center justify-center border border-[#1ba466] p-2 rounded text-[#1ba466] disabled:opacity-50"
                    >
                        {isLoading ? (
                            <LoaderCircle className="animate-spin" size={16} />
                        ) : (
                            "Bật"
                        )}
                    </button>
                ) : (
                    <button
                        className="min-w-[80px] cursor-default flex items-center justify-center border border-[#ff9238] p-2 rounded text-[#ff9238] disabled:opacity-50"
                    >
                        {isLoading ? (
                            <LoaderCircle className="animate-spin" size={16} />
                        ) : (
                            "Tắt"
                        )}
                    </button>
                );
            },
            sortable: false,
            sortBy: "isUse2FA",
        },
        {
            header: t("userManagement.table.status"),
            accessor: (row: UserItem) => {
                const isLoading = statusLoadingIds.has(row.id);

                return row.status ? (
                    <button
                        onClick={() => handleChangeStatusActive(row)}
                        disabled={isLoading}
                        className="min-w-[80px] flex items-center justify-center border border-[#1ba466] p-2 rounded text-[#1ba466] disabled:opacity-50"
                    >
                        {isLoading ? (
                            <LoaderCircle className="animate-spin" size={16} />
                        ) : (
                            t("userManagement.status.active")
                        )}
                    </button>
                ) : (
                    <button
                        onClick={() => handleChangeStatusActive(row)}
                        disabled={isLoading}
                        className="min-w-[80px] flex items-center justify-center border border-[#ff9238] p-2 rounded text-[#ff9238] disabled:opacity-50"
                    >
                        {isLoading ? (
                            <LoaderCircle className="animate-spin" size={16} />
                        ) : (
                            t("userManagement.status.inactive")
                        )}
                    </button>
                );
            },
            sortable: true,
            sortBy: "status",
        },
        {
            header: t("userManagement.table.actions"),
            accessor: (row: UserItem) => (
                <div className="flex gap-2">
                    <ButtonAction
                        onClick={() => { }}
                        icon={<Trash size={14} />}
                        color="red"
                        title={t("userManagement.buttons.delete")}
                    />
                    <ButtonAction
                        onClick={() => handleEdit(row)}
                        icon={<Pencil size={14} />}
                        color="blue"
                        title={t("userManagement.buttons.edit")}
                    />
                    <ButtonAction
                        onClick={() => { handleChangePassword(row) }}
                        icon={<KeyRound size={14} />}
                        title={t("userManagement.buttons.changePassword")}
                    />
                </div>
            ),
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header bg-gray-100">
                <h1 className="text-xl font-semibold">{t("userManagement.list-user")}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateUser)}
                >
                    {t("userManagement.buttons.addNew")}
                </button>
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t("userManagement.modal.confirmDelete.title")}
                    content={t("userManagement.modal.confirmDelete.content")}
                    titleComfirm={t("userManagement.modal.confirmDelete.confirm")}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
            {
                isOpenResetPassword && selectedRow && (
                    <DialogComponent onClose={() => handleCloseResetPassword()} title="Thay đổi mật khẩu" isOpen={isOpenResetPassword} children={<ResetPassword selectedRow={selectedRow} handleCloseResetPassword={handleCloseResetPassword} fetchData={fetchData} />} />
                )
            }
        </div>

    );
}
